/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  InputField,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import {
  ArrowLeft,
  Building2,
  Clock,
  Eye,
  FlaskConical,
  History,
  Loader2,
  Shield,
  Users,
} from 'lucide-react';

import apiResponseHandler from '../../../utils/apiResponseHandler';
import { verificationClient } from '../../../services/verificationClient';
import {
  ActionPolicy,
  OverrideMode,
  PolicyAction,
  PolicyChangeLog,
  PolicyDecision,
  PolicyRequirement,
  RequirementsBlock,
} from '../../../types/VerificationTypes';

import RequirementsBlockView from './components/RequirementsBlockView';
import { buildSatisfiedByMap } from './components/requirementsBlockHelpers';


const POLICIES_LIST_URL = '/admin/security#action-policies';


const OVERRIDE_MODE_LABEL: Record<OverrideMode, string> = {
  replace: 'Replace global default',
  additive: 'Add to global default',
};


function describeOverride(mode: OverrideMode) {
  return mode === 'replace'
    ? 'Throws away the global default and uses this policy verbatim.'
    : 'Layers these requirements on top of the global default (tightens only).';
}


function flattenRequirementCodes(block: RequirementsBlock | Record<string, never>): Set<string> {
  const groups = (block as RequirementsBlock).groups || [];
  return new Set(groups.flatMap((g) => (g.requirements || []).map((r) => r.code)));
}


/**
 * Diff an override's required + step_up credentials against the global default.
 *
 * `added` lists codes the override adds; `removed` lists base codes the
 * override no longer requires (only populated for replace-mode, since
 * additive can only tighten — never remove a base requirement).
 *
 * Step-up codes are folded into the same flat set as required codes — the
 * row-level summary just answers "what credentials does this override
 * mention that the default doesn't?". The override's detail page still
 * shows the full per-block structure.
 */
function computeOverrideDiff(
  override: ActionPolicy,
  base: ActionPolicy
): { added: string[]; removed: string[] } {
  const baseCodes = new Set<string>();
  flattenRequirementCodes(base.required_credentials).forEach((c) => baseCodes.add(c));
  flattenRequirementCodes(base.step_up_credentials).forEach((c) => baseCodes.add(c));
  const overCodes = new Set<string>();
  flattenRequirementCodes(override.required_credentials).forEach((c) => overCodes.add(c));
  flattenRequirementCodes(override.step_up_credentials).forEach((c) => overCodes.add(c));
  const added = Array.from(overCodes).filter((c) => !baseCodes.has(c)).sort();
  if (override.override_mode === 'additive') {
    return { added, removed: [] };
  }
  const removed = Array.from(baseCodes).filter((c) => !overCodes.has(c)).sort();
  return { added, removed };
}


function computeScopeLabel(policy: ActionPolicy): string {
  if (policy.segment_id != null) {
    return `${policy.segment_name || `Segment ${policy.segment_id}`} (segment override)`;
  }
  if (policy.employer_id != null) {
    return `${policy.employer_name || `Employer ${policy.employer_id}`} override`;
  }
  return 'Global default';
}


// Pick the diff baseline for the requirements view. Employer blankets
// diff against the global default; segment-tier rows diff against the
// parent blanket; the global default itself has no diff baseline.
function computeDiffBase(
  policy: ActionPolicy,
  parentBlanket: ActionPolicy | null,
  baseDefault: ActionPolicy | null
): ActionPolicy | null {
  if (policy.segment_id != null) return parentBlanket;
  if (policy.employer_id != null) return baseDefault;
  return null;
}


// ---------------------------------------------------------------------------
// Small layout primitives.
// ---------------------------------------------------------------------------


const Card: React.FC<{
  title?: string;
  titleIcon?: React.ReactNode;
  subtitle?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ title, titleIcon, subtitle, className = '', children }) => (
  <div
    className={`rounded-2xl border border-greyscale-border-default bg-white ${className}`}
  >
    {(title || subtitle) && (
      <div className="border-b border-greyscale-border-default px-md py-sm">
        {title && (
          <h6 className="m-none flex items-center gap-xxs text-base font-semibold text-greyscale-text-title">
            {titleIcon && (
              <span className="inline-flex text-greyscale-text-caption">{titleIcon}</span>
            )}
            {title}
          </h6>
        )}
        {subtitle && (
          <p className="m-none mt-xxxs text-xs text-greyscale-text-caption">{subtitle}</p>
        )}
      </div>
    )}
    <div className="p-md">{children}</div>
  </div>
);


const ReadonlyField: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="flex flex-col gap-xxxs">
    <Label className="text-xs text-greyscale-text-caption">{label}</Label>
    <div className="text-sm text-greyscale-text-body">{value || '—'}</div>
    {hint && <p className="m-none text-xs text-greyscale-text-caption">{hint}</p>}
  </div>
);


// ---------------------------------------------------------------------------
// Test-against-employee panel — calls the chatbot-shape per-action endpoint
// and renders the decision so operators can debug "why can't this employee
// do X" without dropping into shell.
// ---------------------------------------------------------------------------

const DECISION_VARIANT: Record<PolicyDecision['decision'], 'success' | 'failed' | 'processing' | 'pending'> = {
  allow: 'success',
  deny: 'failed',
  remediate: 'processing',
  step_up: 'pending',
};

// ---------------------------------------------------------------------------
// Change log diff renderer — turn the raw {before, after} JSON into a
// scannable per-field "old → new" view. Falls back to JSON for nested
// shapes too large for the inline format.
// ---------------------------------------------------------------------------

function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined) return '∅';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const ChangeLogDiff: React.FC<{
  diff: { before?: Record<string, unknown> | null; after?: Record<string, unknown> | null };
}> = ({ diff }) => {
  const before = (diff.before ?? {}) as Record<string, unknown>;
  const after = (diff.after ?? {}) as Record<string, unknown>;
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();

  if (keys.length === 0) {
    return (
      <p className="m-none text-xs italic text-greyscale-text-caption">
        (No structured diff captured.)
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-xxs">
      {keys.map((key) => {
        const bv = before[key];
        const av = after[key];
        const inBefore = key in before;
        const inAfter = key in after;
        const bs = formatDiffValue(bv);
        const as = formatDiffValue(av);
        if (inBefore && inAfter && bs === as) return null; // unchanged — skip
        return (
          <div key={key} className="text-xs">
            <span className="font-mono font-semibold text-greyscale-text-body">{key}</span>
            <span className="mx-xxxs text-greyscale-text-caption">:</span>
            {inBefore ? (
              <span className="text-error-text-label">
                <code className="font-mono">{bs}</code>
              </span>
            ) : (
              <span className="italic text-greyscale-text-caption">(added)</span>
            )}
            <span className="mx-xxs text-greyscale-text-caption">→</span>
            {inAfter ? (
              <span className="text-success-text-label">
                <code className="font-mono">{as}</code>
              </span>
            ) : (
              <span className="italic text-greyscale-text-caption">(removed)</span>
            )}
          </div>
        );
      })}
    </div>
  );
};


const TestPolicyPanel: React.FC<{ actionCode: string }> = ({ actionCode }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [result, setResult] = useState<PolicyDecision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const idNum = parseInt(employeeId, 10);
    if (Number.isNaN(idNum)) {
      setError('Enter a numeric employee ID.');
      return;
    }
    setError(null);
    setBusy(true);
    setResult(null);
    try {
      const decision = await verificationClient.testPolicyCheck(actionCode, {
        employee_id: idNum,
      });
      setResult(decision);
    } catch (err) {
      apiResponseHandler.handleApiError(err);
      setError('Check failed — see notification.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-end gap-sm">
        <div className="flex-1 max-w-[280px]">
          <InputField
            label="Employee ID"
            type="number"
            value={employeeId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployeeId(e.target.value)}
            placeholder="e.g. 12345"
          />
        </div>
        <Button
          variant="outline"
          onClick={run}
          loading={busy}
          disabled={!employeeId.trim()}
        >
          Run check
        </Button>
      </div>
      {error && (
        <p className="m-none mt-xs text-xs text-error-text-label">{error}</p>
      )}
      {result && (
        <div className="mt-md">
          <div className="mb-sm flex items-center gap-xs">
            <span className="text-xs uppercase tracking-wide text-greyscale-text-caption">
              Decision
            </span>
            <Tag variant={DECISION_VARIANT[result.decision]}>{result.decision}</Tag>
          </div>
          {result.blocked_by.length > 0 && (
            <div className="mb-xs">
              <p className="m-none mb-xxs text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
                Blocked by · hard fails, no in-band remediation
              </p>
              <div className="flex flex-wrap gap-xxs">
                {result.blocked_by.map((c) => (
                  <Tag key={c} variant="failed">{c}</Tag>
                ))}
              </div>
            </div>
          )}
          {result.remediable_gaps.length > 0 && (
            <div className="mb-xs">
              <p className="m-none mb-xxs text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
                Remediable · chatbot can capture
              </p>
              <div className="flex flex-wrap gap-xxs">
                {result.remediable_gaps.map((g) => (
                  <Tag key={g.code} variant="outline">
                    {g.code}
                    {g.flow ? ` → ${g.flow}` : ''}
                  </Tag>
                ))}
              </div>
            </div>
          )}
          {result.step_up_gaps.length > 0 && (
            <div className="mb-xs">
              <p className="m-none mb-xxs text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
                Step-up · re-verify a fresh factor
              </p>
              <div className="flex flex-wrap gap-xxs">
                {result.step_up_gaps.map((g) => (
                  <Tag key={g.code} variant="pending">
                    {g.code}
                    {g.flow ? ` → ${g.flow}` : ''}
                  </Tag>
                ))}
              </div>
            </div>
          )}
          {result.decision === 'allow' && (
            <p className="m-none text-xs italic text-greyscale-text-caption">
              This employee can perform the action right now.
            </p>
          )}
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Breadcrumb chain. Three layouts:
//   * Global default      → "Action Policies > <action> · v<n>"
//   * Employer blanket    → "Action Policies > <action> > <employer>"
//   * Segment-tier policy → "Action Policies > <action> > <employer> > <segment>"
// Each intermediate node links to the corresponding policy when we have
// it, falls back to a non-link page label when we don't.
// ---------------------------------------------------------------------------

const PolicyBreadcrumb: React.FC<{
  policy: ActionPolicy;
  actionDisplayName: string;
  baseDefault: ActionPolicy | null;
  parentBlanket: ActionPolicy | null;
}> = ({ policy, actionDisplayName, baseDefault, parentBlanket }) => {
  const isSegmentTier = policy.segment_id != null;
  const isOverride = policy.employer_id != null;
  const employerLabel = policy.employer_name || `Employer ${policy.employer_id}`;
  const segmentLabel = policy.segment_name || `Segment ${policy.segment_id}`;

  return (
    <Breadcrumb className="mb-md">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/admin/security#action-policies">Action Policies</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        {isOverride ? (
          <>
            <BreadcrumbItem>
              {baseDefault ? (
                <BreadcrumbLink asChild>
                  <Link to={`/admin/verification/policies/${baseDefault.id}`}>
                    {actionDisplayName}
                  </Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{actionDisplayName}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {isSegmentTier && parentBlanket ? (
                <BreadcrumbLink asChild>
                  <Link to={`/admin/verification/policies/${parentBlanket.id}`}>
                    {employerLabel}
                  </Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{employerLabel}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            {isSegmentTier && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{segmentLabel}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </>
        ) : (
          <BreadcrumbItem>
            <BreadcrumbPage>
              {actionDisplayName} · v{policy.version_no}
            </BreadcrumbPage>
          </BreadcrumbItem>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
};


// ---------------------------------------------------------------------------
// Employer overrides table — rendered on a global default page. Each row
// shows one employer's blanket policy with a summary diff vs the default.
// ---------------------------------------------------------------------------

const EmployerOverrideRow: React.FC<{
  sibling: ActionPolicy;
  basePolicy: ActionPolicy;
  onView: (id: number) => void;
}> = ({ sibling, basePolicy, onView }) => {
  const diff = computeOverrideDiff(sibling, basePolicy);
  const hasDiff = diff.added.length > 0 || diff.removed.length > 0;
  return (
    <TableRow>
      <TableCell>
        <span className="text-xs">
          {sibling.employer_name || `Employer ${sibling.employer_id}`}
        </span>
      </TableCell>
      <TableCell>
        {hasDiff ? (
          <div className="flex flex-col gap-xxxs">
            {diff.added.length > 0 && (
              <div className="text-xs text-success-text-label">
                requires{' '}
                {diff.added.map((code, i) => (
                  <React.Fragment key={`add-${code}`}>
                    {i > 0 && ', '}
                    <code className="font-mono">{code}</code>
                  </React.Fragment>
                ))}
              </div>
            )}
            {diff.removed.length > 0 && (
              <div className="text-xs text-error-text-label">
                waives{' '}
                {diff.removed.map((code, i) => (
                  <React.Fragment key={`rem-${code}`}>
                    {i > 0 && ', '}
                    <code className="font-mono">{code}</code>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs italic text-greyscale-text-caption">
            matches default
          </span>
        )}
      </TableCell>
      <TableCell>v{sibling.version_no}</TableCell>
      <TableCell className="text-xs">
        {new Date(sibling.effective_from).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-right">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`View employer ${sibling.employer_id} override`}
              onClick={() => onView(sibling.id)}
            >
              <Eye size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View</TooltipContent>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};


const EmployerOverridesTable: React.FC<{
  siblings: ActionPolicy[];
  basePolicy: ActionPolicy;
  onView: (id: number) => void;
}> = ({ siblings, basePolicy, onView }) => {
  if (siblings.length === 0) {
    return (
      <p className="m-none text-sm italic text-greyscale-text-caption">
        No employer overrides for this action.
      </p>
    );
  }
  return (
    <TooltipProvider delayDuration={150}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Employer</TableHead>
            <TableHead>Diff from default</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Effective from</TableHead>
            <TableHead className="w-[80px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {siblings.map((sibling) => (
            <EmployerOverrideRow
              key={sibling.id}
              sibling={sibling}
              basePolicy={basePolicy}
              onView={onView}
            />
          ))}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};


// ---------------------------------------------------------------------------
// Segment overrides table — rendered on an employer-blanket page. Lists
// segment-tier rows that refine the blanket for a sub-group, sorted by
// rank then id. Always additive over the blanket (the editor rejects
// replace mode at the segment tier).
// ---------------------------------------------------------------------------

const SegmentOverrideRow: React.FC<{
  segment: ActionPolicy;
  basePolicy: ActionPolicy;
  onView: (id: number) => void;
}> = ({ segment, basePolicy, onView }) => {
  // Segment-tier rows are additive-only — the diff helper will return
  // `removed: []`, but we still run it for consistency with the
  // employer-overrides row layout.
  const diff = computeOverrideDiff(segment, basePolicy);
  return (
    <TableRow>
      <TableCell>
        <span className="text-xs">
          {segment.segment_name || `Segment ${segment.segment_id}`}
        </span>
      </TableCell>
      <TableCell>
        {diff.added.length === 0 ? (
          <span className="text-xs italic text-greyscale-text-caption">
            matches baseline
          </span>
        ) : (
          <div className="text-xs text-success-text-label">
            requires{' '}
            {diff.added.map((code, i) => (
              <React.Fragment key={`seg-${segment.id}-${code}`}>
                {i > 0 && ', '}
                <code className="font-mono">{code}</code>
              </React.Fragment>
            ))}
          </div>
        )}
      </TableCell>
      <TableCell>{segment.segment_rank}</TableCell>
      <TableCell className="text-xs">
        {new Date(segment.effective_from).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-right">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`View segment ${segment.segment_id} override`}
              onClick={() => onView(segment.id)}
            >
              <Eye size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View</TooltipContent>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};


const SegmentOverridesTable: React.FC<{
  segmentChildren: ActionPolicy[];
  basePolicy: ActionPolicy;
  onView: (id: number) => void;
}> = ({ segmentChildren, basePolicy, onView }) => {
  if (segmentChildren.length === 0) {
    return (
      <p className="m-none text-sm italic text-greyscale-text-caption">
        No segment overrides yet. Add one to apply different requirements to a sub-group of this
        employer&apos;s workforce.
      </p>
    );
  }
  const sorted = segmentChildren
    .slice()
    .sort((a, b) => a.segment_rank - b.segment_rank || a.id - b.id);
  return (
    <TooltipProvider delayDuration={150}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Segment</TableHead>
            <TableHead>Adds</TableHead>
            <TableHead>Rank</TableHead>
            <TableHead>Effective from</TableHead>
            <TableHead className="w-[80px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((seg) => (
            <SegmentOverrideRow
              key={seg.id}
              segment={seg}
              basePolicy={basePolicy}
              onView={onView}
            />
          ))}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};


// ---------------------------------------------------------------------------
// Change-log table — lists every admin edit on this policy with an
// expandable per-row diff (delegates to ChangeLogDiff for the inner
// before/after layout).
// ---------------------------------------------------------------------------

const ChangeLogTable: React.FC<{
  entries: PolicyChangeLog[];
  expandedId: string | null;
  onToggleExpand: (id: string | null) => void;
}> = ({ entries, expandedId, onToggleExpand }) => {
  if (entries.length === 0) {
    return (
      <p className="m-none text-sm italic text-greyscale-text-caption">
        No changes recorded yet.
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>When</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Actor</TableHead>
          <TableHead>Reason</TableHead>
          <TableHead className="w-[80px] text-right">Diff</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => (
          <React.Fragment key={entry.id}>
            <TableRow>
              <TableCell className="text-xs whitespace-nowrap">
                {new Date(entry.date_created).toLocaleString()}
              </TableCell>
              <TableCell>
                <Tag variant="outline">{entry.change_action}</Tag>
              </TableCell>
              <TableCell className="text-xs">{entry.actor}</TableCell>
              <TableCell className="text-xs">{entry.reason}</TableCell>
              <TableCell className="text-right">
                <Button
                  variant="link"
                  size="xs"
                  onClick={() => onToggleExpand(expandedId === entry.id ? null : entry.id)}
                >
                  {expandedId === entry.id ? 'hide' : 'show'}
                </Button>
              </TableCell>
            </TableRow>
            {expandedId === entry.id && (
              <TableRow>
                <TableCell colSpan={5} className="bg-greyscale-surface-subtle">
                  <ChangeLogDiff diff={entry.diff} />
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
};


const PolicyDetailPage: React.FC = () => {
  const { policyId } = useParams<{ policyId: string }>();
  const history = useHistory();

  const [policy, setPolicy] = useState<ActionPolicy | null>(null);
  const [baseDefault, setBaseDefault] = useState<ActionPolicy | null>(null);
  const [actions, setActions] = useState<PolicyAction[]>([]);
  const [requirements, setRequirements] = useState<PolicyRequirement[]>([]);
  const [loading, setLoading] = useState(true);

  const [siblings, setSiblings] = useState<ActionPolicy[]>([]);
  // Segment children — populated when viewing an employer blanket. These
  // are the segment-tier rows that refine the blanket for a sub-group of
  // the employer's workforce. Empty list = no segment overrides
  // authored yet.
  const [segmentChildren, setSegmentChildren] = useState<ActionPolicy[]>([]);
  // Parent blanket — populated when viewing a segment-tier row. The diff
  // and the breadcrumb both need it.
  const [parentBlanket, setParentBlanket] = useState<ActionPolicy | null>(null);
  const [changeLog, setChangeLog] = useState<PolicyChangeLog[]>([]);
  const [expandedChangeLog, setExpandedChangeLog] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const idNum = parseInt(policyId, 10);

      const [p, acts, reqs] = await Promise.all([
        verificationClient.getPolicy(idNum),
        verificationClient.listActions(),
        verificationClient.listRequirements(),
      ]);
      setPolicy(p);
      setActions(acts);
      setRequirements(reqs);

      const isSegmentTier = p.segment_id != null;
      const isEmployerBlanket = p.employer_id != null && p.segment_id == null;
      const isGlobalDefault = p.employer_id == null;

      // For the global default: list every employer blanket as a sibling
      // (excluding self + segment-tier rows, which are children of their
      // blanket and surfaced on THAT page instead).
      const siblingsPromise: Promise<ActionPolicy[]> = isGlobalDefault
        ? verificationClient
            .listPolicies({ action_code: p.action_code })
            .then((all) => all.filter((s) => s.id !== p.id && s.segment_id == null))
        : Promise.resolve([]);

      // For an employer blanket: fetch its segment-tier children.
      const segmentChildrenPromise: Promise<ActionPolicy[]> = isEmployerBlanket
        ? verificationClient
            .listPolicies({ action_code: p.action_code, employer_id: p.employer_id ?? undefined })
            .then((all) => all.filter((s) => s.segment_id != null))
        : Promise.resolve([]);

      // Diff baseline:
      //   * employer blanket → diff against global default.
      //   * segment tier     → diff against parent employer blanket.
      // Both fetched as `parentBlanket` for the segment tier; the
      // existing baseDefault state stays for blanket-tier diffs.
      const basePromise: Promise<ActionPolicy | null> = isEmployerBlanket
        ? verificationClient
            .listPolicies({ action_code: p.action_code })
            .then(
              (all) =>
                all.find((s) => s.employer_id === null && s.segment_id == null) || null,
            )
        : Promise.resolve(null);

      const parentBlanketPromise: Promise<ActionPolicy | null> = isSegmentTier
        ? verificationClient
            .listPolicies({ action_code: p.action_code, employer_id: p.employer_id ?? undefined })
            .then((all) => all.find((s) => s.segment_id == null) || null)
        : Promise.resolve(null);

      const [sibs, log, base, segChildren, parent] = await Promise.all([
        siblingsPromise,
        verificationClient.listPolicyChangeLog({
          entity_type: 'action_policy',
          entity_id: String(p.id),
        }),
        basePromise,
        segmentChildrenPromise,
        parentBlanketPromise,
      ]);
      setSiblings(sibs);
      setChangeLog(log.results);
      setBaseDefault(base);
      setSegmentChildren(segChildren);
      setParentBlanket(parent);
    } catch (err) {
      apiResponseHandler.handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  useEffect(() => {
    load();
  }, [load]);

  const action = useMemo(() => {
    if (!policy) return undefined;
    return actions.find((a) => a.code === policy.action_code);
  }, [actions, policy]);
  const actionDisplayName = action?.name || policy?.action_code || '';
  const actionDescription = action?.description || '';

  if (loading || !policy) {
    return (
      <div className="flex justify-center p-xl">
        <Loader2 className="animate-spin text-secondary-text-label" size={32} />
      </div>
    );
  }

  const isSegmentTier = policy.segment_id != null;
  const isEmployerBlanket = policy.employer_id != null && policy.segment_id == null;
  // `isOverride` covers both employer blanket and segment tier — anything
  // that isn't the global default. Existing layout branches use this to
  // show the Override settings card and the "diff against base" diff
  // mode. Segment-tier rows additionally swap the diff baseValue to the
  // parent blanket via computeDiffBase.
  const isOverride = policy.employer_id != null;
  const scopeLabel = computeScopeLabel(policy);
  const diffBase = computeDiffBase(policy, parentBlanket, baseDefault);

  // Requirement registry by code — used by the requirement views to render
  // rows (icon + code + description) and to drive the hard / remediable split
  // on the Required section + TTL badges on the Step-up section.
  const requirementsByCode = new Map(requirements.map((r) => [r.code, r]));
  // Inverse of PolicyRequirement.satisfies — drives the "also satisfied by"
  // tooltip hint on each chip. The forward direction is stored on the model;
  // the inverse is recomputed here from the same registry fetch.
  const satisfiedByCode = buildSatisfiedByMap(requirements);
  const navigateToPolicy = (id: number) =>
    history.push(`/admin/verification/policies/${id}`);

  return (
    <div className="min-h-full bg-white p-md">
      {/* Banner header — audit-log style */}
      <div
        className="-mx-md -mt-md mb-sm flex flex-wrap items-center justify-between gap-sm px-md py-lg"
        style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}
      >
        <div>
          <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
            {actionDisplayName}
          </h1>
          {actionDescription && (
            <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
              {actionDescription}
            </p>
          )}
          <div className="mt-xs flex flex-wrap items-center gap-xxs">
            <Tag variant="outline">{scopeLabel}</Tag>
            <Tag variant="outline">v{policy.version_no}</Tag>
          </div>
        </div>
      </div>

      <PolicyBreadcrumb
        policy={policy}
        actionDisplayName={actionDisplayName}
        baseDefault={baseDefault}
        parentBlanket={parentBlanket}
      />

      {/* Requirements (Settings card only renders for overrides — global
          defaults have nothing left in Settings now that Schedule is gone). */}
      <div className={`grid grid-cols-1 gap-md ${isOverride ? 'lg:grid-cols-12' : ''}`}>
        <div className={`flex flex-col gap-md ${isOverride ? 'lg:col-span-8' : ''}`}>
          <Card
            title="Required credentials"
            titleIcon={<Shield size={16} />}
            subtitle="Every requirement here must be held for the action to be allowed."
          >
            <RequirementsBlockView
              value={policy.required_credentials}
              defaultOperator="AND"
              emptyHint="No requirements — this policy is permissive on this dimension."
              baseValue={diffBase ? diffBase.required_credentials : undefined}
              overrideMode={isOverride ? policy.override_mode : undefined}
              requirementsByCode={requirementsByCode}
              satisfiedByCode={satisfiedByCode}
            />
          </Card>

          <Card
            title="Step-up verification"
            titleIcon={<Clock size={16} />}
            subtitle="On top of being held, these must be fresh (within their TTL). If they're stale, the decision becomes step_up instead of allow."
          >
            <RequirementsBlockView
              value={policy.step_up_credentials}
              defaultOperator="OR"
              emptyHint="No step-up requirements."
              baseValue={diffBase ? diffBase.step_up_credentials : undefined}
              overrideMode={isOverride ? policy.override_mode : undefined}
              requirementsByCode={requirementsByCode}
              satisfiedByCode={satisfiedByCode}
              isStepUp
            />
          </Card>
        </div>

        {isOverride && (
          <div className="lg:col-span-4">
            <Card title="Override">
              <div className="flex flex-col gap-sm">
                <ReadonlyField
                  label="Override behaviour"
                  value={
                    isSegmentTier
                      ? 'Add to employer override (additive only)'
                      : OVERRIDE_MODE_LABEL[policy.override_mode]
                  }
                  hint={
                    isSegmentTier
                      ? 'Segment-tier overrides can only tighten the employer baseline. To loosen for a sub-group, adjust the employer override instead.'
                      : describeOverride(policy.override_mode)
                  }
                />
                {isSegmentTier && (
                  <ReadonlyField
                    label="Segment rank"
                    value={policy.segment_rank}
                    hint="Lower wins when multiple segments match the same employee. Today's composition is additive (commutative) so rank only affects display order — load-bearing if a future segment-tier replace mode lands."
                  />
                )}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Employer overrides — only shown on the global default */}
      {!isOverride && (
        <Card
          className="mt-md"
          title="Employer overrides"
          titleIcon={<Building2 size={16} />}
          subtitle="Employer-specific versions of this policy. The global default above applies to any employer without an override."
        >
          <EmployerOverridesTable
            siblings={siblings}
            basePolicy={policy}
            onView={navigateToPolicy}
          />
        </Card>
      )}

      {/* Segment overrides — only shown on an employer blanket. Lists
          segment-tier rows that refine this blanket for a sub-group of
          the employer's workforce. Always additive (the editor rejects
          replace at this tier; see verification serializer). */}
      {isEmployerBlanket && (
        <Card
          className="mt-md"
          title="Segment overrides"
          titleIcon={<Users size={16} />}
          subtitle="Refinements that apply only to employees who match a specific segment. Always additive over this employer baseline — segments tighten, never loosen."
        >
          <SegmentOverridesTable
            segmentChildren={segmentChildren}
            basePolicy={policy}
            onView={navigateToPolicy}
          />
        </Card>
      )}

      {/* Test against employee */}
      <Card
        className="mt-md"
        title="Test against employee"
        titleIcon={<FlaskConical size={16} />}
        subtitle="Run the evaluator against a specific employee to see what decision they'd get. Useful for support triage — &quot;why can't this employee do X?&quot;"
      >
        <TestPolicyPanel actionCode={policy.action_code} />
      </Card>

      {/* Change log */}
      <Card
        className="mt-md"
        title="Change log"
        titleIcon={<History size={16} />}
        subtitle="Every admin edit on this policy. Each row carries the actor and a required reason for audit."
      >
        <ChangeLogTable
          entries={changeLog}
          expandedId={expandedChangeLog}
          onToggleExpand={setExpandedChangeLog}
        />
      </Card>

      <div className="mt-lg inline-block">
        <Button
          variant="outline"
          leftIcon={<ArrowLeft size={16} />}
          onClick={() => history.push(POLICIES_LIST_URL)}
        >
          Back to Action Policies
        </Button>
      </div>
    </div>
  );
};

export default PolicyDetailPage;
